import { useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { deepRemoveKeys } from '../../lib/deepRemoveKeys';
import { hrefFor } from '../../routers';
import { useCart } from '../../hooks/useCart';
import { OrderSummary } from '../../hooks/useOrderSummary';
import { OrderState } from '../../hooks/useUpdateOrder';
import { useCountry } from '../CountrySelector';

const SUPPORTED_CARD_NETWORKS = ['amex', 'discover', 'masterCard', 'visa'];
const MERCHANT_CAPABILITIES: ApplePayJS.ApplePayMerchantCapability[] = ['supports3DS'];
const REQUIRED_SHIPPING_CONTACT_FIELDS: ApplePayJS.ApplePayContactField[] = [
  'postalAddress',
  'email'
];
const REQUIRED_BILLING_CONTACT_FIELDS: ApplePayJS.ApplePayContactField[] = ['postalAddress'];

export const useApplePayButton = () => {
  const {
    cart: {
      attributes: { countryCode, applePaymentSummary }
    }
  } = useCart();

  const { pathname } = useLocation();

  const {
    country: { currencyCode }
  } = useCountry();

  const getApplePayCheckoutSession = useCallback(() => {
    const APPLE_PAY_VERSION = ApplePaySession.supportsVersion(2) ? 2 : 1;

    const PAYMENT_REQUEST = applePaymentSummary?.data
      ? {
          countryCode,
          currencyCode,
          shippingMethods: applePaymentSummary.data.shippingMethods,
          lineItems: applePaymentSummary.data.lineItems,
          total: applePaymentSummary.data.total,
          supportedNetworks: SUPPORTED_CARD_NETWORKS,
          merchantCapabilities: MERCHANT_CAPABILITIES,
          requiredShippingContactFields: REQUIRED_SHIPPING_CONTACT_FIELDS,
          requiredBillingContactFields: REQUIRED_BILLING_CONTACT_FIELDS,
          ...(pathname === hrefFor.CheckoutPage() &&
            applePaymentSummary.data.shippingContact &&
            applePaymentSummary.data.shippingContact.emailAddress && {
              shippingContact: applePaymentSummary.data.shippingContact
            })
        }
      : null;

    const NEW_PAYMENT_REQUEST = deepRemoveKeys(PAYMENT_REQUEST, '__typename');

    return { version: APPLE_PAY_VERSION, request: NEW_PAYMENT_REQUEST };
  }, [applePaymentSummary, countryCode, currencyCode, pathname]);

  const getApplePayOrderSession = useCallback((order: OrderSummary) => {
    const APPLE_PAY_VERSION = ApplePaySession.supportsVersion(2) ? 2 : 1;

    const orderState = order.attributes.orderState;
    const priceToPayWithApplePay =
      orderState === OrderState.Default
        ? order.attributes.paymentSummary.remainingBalance.price
        : order.attributes.paymentSummary.payToday.price;

    const PAYMENT_REQUEST = {
      countryCode: order.attributes.address.countryCode,
      currencyCode: order.attributes.currency,
      total: {
        label: 'Moda Operandi, Inc.',
        amount: `${priceToPayWithApplePay}`
      },
      supportedNetworks: SUPPORTED_CARD_NETWORKS,
      merchantCapabilities: MERCHANT_CAPABILITIES,
      requiredShippingContactFields: REQUIRED_SHIPPING_CONTACT_FIELDS,
      requiredBillingContactFields: REQUIRED_BILLING_CONTACT_FIELDS
    };

    return { version: APPLE_PAY_VERSION, request: PAYMENT_REQUEST };
  }, []);

  return { getApplePayCheckoutSession, getApplePayOrderSession };
};
